import {
  endOfMonth,
  endOfQuarter,
  endOfYear,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subYears,
} from "date-fns";

import type { DateRange, DateRangePreset } from "@/components/ui/date-picker";

import { getTimezone } from "@/lib/timezone";
import { zonedTodayMarker } from "@/lib/timezone-dates";

/**
 * "Today" as a calendar-day marker in the clinic's effective timezone (not the
 * browser's). Its local y/m/d encode the effective-zone date, so the day-only
 * serializer (`pickedDay`) that feeds report windows resolves to the
 * clinic's calendar day. All month/day math below is derived from this marker.
 */
function effectiveToday(timeZone: string): Date {
  return zonedTodayMarker(timeZone);
}

/**
 * Get the start date for "Month to Date" (first day of the current month)
 */
export function getMonthToDateStart(timeZone: string = getTimezone()): Date {
  const today = effectiveToday(timeZone);
  return new Date(today.getFullYear(), today.getMonth(), 1);
}

/**
 * Standard date range presets for dashboards
 * Includes Month to Date, Year to Date, Today, Yesterday, and common day ranges
 */
export function getStandardDateRangePresets(
  timeZone: string = getTimezone(),
): DateRangePreset[] {
  const today = effectiveToday(timeZone);
  const monthToDateStart = getMonthToDateStart(timeZone);
  const yearToDateStart = startOfYear(today);
  const lastMonthStart = startOfMonth(subMonths(today, 1));
  const lastMonthEnd = endOfMonth(subMonths(today, 1));
  const lastQuarterStart = startOfQuarter(subQuarters(today, 1));
  const lastQuarterEnd = endOfQuarter(subQuarters(today, 1));
  const lastYearStart = startOfYear(subYears(today, 1));
  const lastYearEnd = endOfYear(subYears(today, 1));

  return [
    {
      label: "Month to Date",
      dateRange: {
        from: monthToDateStart,
        to: today,
      },
    },
    {
      label: "Year to Date",
      dateRange: {
        from: yearToDateStart,
        to: today,
      },
    },
    {
      label: "Last Month",
      dateRange: {
        from: lastMonthStart,
        to: lastMonthEnd,
      },
    },
    {
      label: "Last Quarter",
      dateRange: {
        from: lastQuarterStart,
        to: lastQuarterEnd,
      },
    },
    {
      label: "Last Year",
      dateRange: {
        from: lastYearStart,
        to: lastYearEnd,
      },
    },
    {
      label: "Today",
      dateRange: {
        from: today,
        to: today,
      },
    },
    {
      label: "Yesterday",
      dateRange: {
        from: subDays(today, 1),
        to: subDays(today, 1),
      },
    },
    {
      label: "Last 7 days",
      dateRange: {
        from: subDays(today, 6),
        to: today,
      },
    },
    {
      label: "Last 14 days",
      dateRange: {
        from: subDays(today, 13),
        to: today,
      },
    },
    {
      label: "Last 30 days",
      dateRange: {
        from: subDays(today, 29),
        to: today,
      },
    },
    {
      label: "Last 90 days",
      dateRange: {
        from: subDays(today, 89),
        to: today,
      },
    },
  ];
}

/**
 * Get the default date range (Month to Date)
 */
export function getDefaultDateRange(
  timeZone: string = getTimezone(),
): DateRange {
  const today = effectiveToday(timeZone);
  const monthToDateStart = getMonthToDateStart(timeZone);

  return {
    from: monthToDateStart,
    to: today,
  };
}
